"use client";

import { useEffect, useLayoutEffect } from "react";
import { deviceDetector } from "@ospweb/sdk-ui";
import type { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import {
  getFixedAndroidSafeAreaInsetsByVendor,
  getSafeAreaModeFromUA,
  getAndroidVendorFromUA,
  getDefaultAndroidSafeAreaInsetsByVendor,
  IOS_DEFAULT_SAFE_AREA_INSETS,
  type SafeAreaMode,
  type SafeAreaInsets,
} from "@/src/utils/safeAreaPolicy";
import {
  isValidInsetNumber,
  readSafeAreaCache,
  readSafeAreaInsetsFromCSSVars,
  writeSafeAreaCache,
  writeSafeAreaCSSVars,
} from "@/src/utils/safeAreaRuntime";
import { SAFE_AREA_STARTUP_SCRIPT } from "@/src/components/IOSPWAStyleHandler.safeAreaStartupScript";

const getAndroidVendorRuntime = (): ReturnType<typeof getAndroidVendorFromUA> =>
  getAndroidVendorFromUA(
    typeof navigator !== "undefined" ? navigator.userAgent || "" : "",
  );
const getSafeAreaModeRuntime = (): SafeAreaMode =>
  getSafeAreaModeFromUA(
    typeof navigator !== "undefined" ? navigator.userAgent || "" : "",
  );

/**
 * iOS PWA 样式处理器
 * 在 iOS PWA 环境中添加类名，用于禁用长按预览弹窗
 */
export const IOSPWAStyleHandler = ({
  deviceType,
}: {
  deviceType?: DeviceType;
}) => {
  const isProbablyInNativeWebView = () => {
    if (typeof window === "undefined") {
      return false;
    }
    // ✅ 最稳判断：Android 原生通过 addJavascriptInterface 注入的 bridge
    // 浏览器环境一般不存在（除非刻意 mock），误判概率极低
    const w = window as unknown as { bridge?: unknown; webkit?: unknown };
    if (typeof w.bridge === "object" && w.bridge !== null) {
      return true;
    }

    const ua =
      typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroidWebView =
      isAndroid && (/\bwv\b/i.test(ua) || /Version\/\d+\.\d+/i.test(ua));
    const isIOSWebView =
      isIOS &&
      !!(
        (w.webkit as any)?.messageHandlers ||
        (window as any).webkit?.messageHandlers
      );
    return isAndroidWebView || isIOSWebView;
  };

  useLayoutEffect(() => {
    // 兜底：如果 beforeInteractive 脚本因为任何原因没有生效，
    // 在首次渲染前尽可能设置一个“常规默认值”，减少顶部闪动。
    const applyRuntimeStartupSafeArea = () => {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return;
      }
      const safeAreaMode = getSafeAreaModeRuntime();

      // fixed 模式：完全信任 Web 固定值，不接受原生注入/缓存覆盖
      if (safeAreaMode === "fixed") {
        writeSafeAreaCSSVars(getFixedSafeAreaInsetsRuntime());
        return;
      }

      // 优先用服务端路由参数 [device]（更早、更确定），再兜底用客户端 detector
      const isInApp =
        deviceType === "android" ||
        deviceType === "ios" ||
        // 注意：deviceDetector 的 isAndroidApp/isIOSApp 在“原生设备信息尚未注入”时可能为 false
        // 增加 UA/WebView 兜底，避免启动竞态导致 top=0 一直不恢复
        isProbablyInNativeWebView() ||
        deviceDetector.isAndroidApp() ||
        deviceDetector.isIOSApp();
      if (!isInApp) {
        return;
      }

      const root = document.documentElement;
      const style = getComputedStyle(root);
      const curTop = parseInt(
        style.getPropertyValue("--safe-area-inset-top") || "0",
        10,
      );
      const curBottom = parseInt(
        style.getPropertyValue("--safe-area-inset-bottom") || "0",
        10,
      );

      // 如果已有有效值（env() 或原生已经覆盖），不要打断
      if (curTop > 0 || curBottom > 0) {
        return;
      }

      // 优先读取缓存（上一次原生注入后的值），避免每次启动都跳动
      const cached = readSafeAreaCache();
      if (cached) {
        writeSafeAreaCSSVars(cached);
        return;
      }

      // 否则应用常规默认值（iOS/Android 分别处理）
      writeSafeAreaCSSVars(getDefaultSafeAreaInsetsRuntime());
    };

    applyRuntimeStartupSafeArea();
  }, [deviceType]);

  useEffect(() => {
    // 运行期监听：当原生端真正注入后，缓存下来，下次启动先用缓存值顶上
    if (typeof window === "undefined") {
      return;
    }

    // 同样优先用服务端路由参数判断（避免非 app 环境无谓监听/缓存）
    const shouldListen =
      deviceType === "android" ||
      deviceType === "ios" ||
      isProbablyInNativeWebView() ||
      deviceDetector.isAndroidApp() ||
      deviceDetector.isIOSApp();
    if (!shouldListen) {
      return;
    }
    const safeAreaMode = getSafeAreaModeRuntime();
    if (safeAreaMode === "fixed") {
      writeSafeAreaCSSVars(getFixedSafeAreaInsetsRuntime());
      return;
    }

    const handler = (event: Event) => {
      try {
        const customEvent = event as CustomEvent<SafeAreaInsets>;
        const detail = customEvent.detail;
        if (
          detail &&
          isValidInsetNumber(detail.top) &&
          isValidInsetNumber(detail.right) &&
          isValidInsetNumber(detail.bottom) &&
          isValidInsetNumber(detail.left)
        ) {
          // 额外兜底：确保 CSS 变量与事件一致（避免“只派发事件但没写 CSS 变量”的异常情况）
          writeSafeAreaCSSVars(detail);
          // 不缓存全 0（避免“0 污染”导致下次启动/后续一直为 0）
          if (detail.top > 0 || detail.bottom > 0) {
            writeSafeAreaCache(detail);
          }
          return;
        }

        // 没有 detail 时，从 CSS 变量读取并缓存
        const insets = readSafeAreaInsetsFromCSSVars();
        if (insets.top > 0 || insets.bottom > 0) {
          writeSafeAreaCache(insets);
        }
      } catch {
        // ignore
      }
    };

    window.addEventListener("safe-area-insets-changed", handler);
    return () => {
      window.removeEventListener("safe-area-insets-changed", handler);
    };
  }, [deviceType]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    let rafId: number | null = null;
    let isCleanedUp = false;

    const applyIOSPWAClass = () => {
      // 如果已经清理，不再执行
      if (isCleanedUp) {
        return;
      }

      // 确保 document.body 存在
      if (typeof document === "undefined" || !document.body) {
        return;
      }

      // 检测是否为 iOS App/PWA 环境
      // 使用 isIOSApp() 而不是 isIOSDevice() + checkIfPWA()
      // 因为 isIOSApp() 已经包含了 PWA 检测，并且优先检测 webkit.messageHandlers
      // 这对于现代 iPadOS（UA 可能伪装成 Mac）更准确
      const isIOSApp = deviceDetector.isIOSApp();
      const isIOSDevice = deviceDetector.isIOSDevice();
      const isPWA = deviceDetector.checkIfPWA();

      // 如果是 iOS App（包括 PWA），或者 iOS 设备且是 PWA，则添加类名
      // 这样可以覆盖 iPad 在 PWA 模式下的情况
      if (isIOSApp || (isIOSDevice && isPWA)) {
        // 在 body 上添加类名，用于 CSS 选择器
        // 使用 add 方法，即使类名已存在也不会报错
        document.body.classList.add("ios-pwa-mode");
      } else {
        // 如果不是 iOS PWA，移除类名（防止状态变化）
        document.body.classList.remove("ios-pwa-mode");
      }
    };

    const handleDOMReady = () => {
      applyIOSPWAClass();
    };

    // 如果 body 已经存在，立即执行
    if (document.body) {
      applyIOSPWAClass();
    } else {
      // 如果 body 还没准备好，等待 DOM 加载完成
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", handleDOMReady);
      } else {
        // 如果 DOM 已经加载完成，使用 setTimeout 确保在下一事件循环执行
        timeoutId = setTimeout(handleDOMReady, 800);
      }

      // 也使用 requestAnimationFrame 作为备用方案
      rafId = requestAnimationFrame(() => {
        applyIOSPWAClass();
      });
    }

    // 统一的清理函数：组件卸载时移除类名和清理所有监听器
    return () => {
      isCleanedUp = true;

      // 清理事件监听器
      document.removeEventListener("DOMContentLoaded", handleDOMReady);

      // 清理定时器
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      // 清理 requestAnimationFrame
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      // 移除类名
      if (document.body) {
        document.body.classList.remove("ios-pwa-mode");
      }
    };
  }, []);

  // 启动脚本自身会判断是否在 App WebView；即使 deviceType 偶发为 h5/缺失，也能在 WebView 内生效
  return (
    <script
      id="safe-area-startup-defaults"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: SAFE_AREA_STARTUP_SCRIPT }}
    />
  );
};

function getDefaultSafeAreaInsetsRuntime(): SafeAreaInsets {
  // 仅用于“原生注入前”的常规默认值：宁可偏保守，避免内容被刘海/状态栏遮挡。
  if (deviceDetector.isIOSApp()) {
    return IOS_DEFAULT_SAFE_AREA_INSETS;
  }

  if (deviceDetector.isAndroidApp()) {
    const vendor = getAndroidVendorRuntime();
    return getDefaultAndroidSafeAreaInsetsByVendor(vendor);
  }

  return { top: 0, right: 0, bottom: 0, left: 0 };
}

function getFixedSafeAreaInsetsRuntime(): SafeAreaInsets {
  const vendor = getAndroidVendorRuntime();
  return getFixedAndroidSafeAreaInsetsByVendor(vendor);
}
